/**
 * Gallery Module
 * Handles image gallery with native <dialog> lightbox
 */

import { openDialog, closeDialog } from './dialogs.js';

let currentImageIndex = 0;
let images = [];
let isAnimating = false;

export function initGallery() {
    const galleryContainer = document.querySelector('[data-gallery-images]');
    if (!galleryContainer) return;

    // Read image data from DOM (set by EE template via data attribute)
    try {
        images = JSON.parse(galleryContainer.dataset.galleryImages);
    } catch {
        // Fallback: scrape from thumbnail elements
        images = Array.from(document.querySelectorAll('.gallery-thumb img')).map(img => ({
            src: img.dataset.fullSrc || img.src,
            alt: img.alt || ''
        }));
    }

    if (!images.length) return;

    // Start on hero image if specified, otherwise first image
    const heroIndex = parseInt(galleryContainer.dataset.heroIndex) || 0;
    currentImageIndex = heroIndex;
    updateMainGalleryImage(currentImageIndex);

    // Thumbnail clicks update the hero image, except the "+X more" badge which opens the lightbox
    document.querySelectorAll('.gallery-thumb').forEach(thumb => {
        thumb.addEventListener('click', (event) => {
            const imageIndex = parseInt(thumb.dataset.imageIndex) || 0;

            if (event.target.closest('[data-gallery-open]')) {
                openGallery(currentImageIndex);
                return;
            }

            updateMainGalleryImage(imageIndex);
        });
    });

    document.querySelectorAll('.gallery-main [data-gallery-open]').forEach(trigger => {
        trigger.addEventListener('click', () => {
            openGallery(currentImageIndex);
        });
    });

    // Navigation buttons
    const prevBtn = document.querySelector('.gallery-prev');
    const nextBtn = document.querySelector('.gallery-next');

    if (prevBtn) {
        prevBtn.addEventListener('click', () => navigateGallery(-1));
    }

    if (nextBtn) {
        nextBtn.addEventListener('click', () => navigateGallery(1));
    }

    const dialog = document.getElementById('gallery-dialog');
    if (dialog) {
        dialog.addEventListener('click', (event) => {
            const keepOpenTarget = event.target.closest(
                '.gallery-dialog-image, .gallery-dialog-ghost, .gallery-dialog-thumbnails, .gallery-dialog-thumb, .gallery-dialog-header, .gallery-dialog-nav, .gallery-close, .gallery-prev, .gallery-next'
            );

            if (keepOpenTarget) {
                return;
            }

            closeDialog(dialog);
        });
    }

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
        const dialog = document.getElementById('gallery-dialog');
        if (!dialog || !dialog.open) return;

        if (e.key === 'ArrowLeft') {
            navigateGallery(-1);
        } else if (e.key === 'ArrowRight') {
            navigateGallery(1);
        }
    });

    // Touch/swipe support (basic)
    setupSwipeGestures();

    // Handle broken gallery images — hide them so placeholder backgrounds show
    document.querySelectorAll('.gallery-thumb img, .gallery-main-image').forEach(img => {
        img.addEventListener('error', () => img.classList.add('is-broken'));
        if (img.complete && img.naturalWidth === 0) img.classList.add('is-broken');
    });

    window.addEventListener('resize', () => {
        const dialog = document.getElementById('gallery-dialog');
        if (dialog?.open) {
            syncThumbnailAlignment();
        }
    });
}

/**
 * Open gallery at specific image index
 */
function openGallery(imageIndex = 0) {
    currentImageIndex = imageIndex;

    showImage(currentImageIndex);

    // Populate thumbnails
    populateThumbnails();

    // Open dialog
    openDialog('gallery-dialog', {
        onOpen: () => {
            requestAnimationFrame(syncThumbnailAlignment);
        }
    });
}

/**
 * Show image at specific index
 */
function showImage(index) {
    const dialog = document.getElementById('gallery-dialog');
    const dialogImage = dialog.querySelector('.gallery-dialog-image');

    if (!images[index]) {
        console.error('Image index out of bounds:', index);
        return;
    }

    dialogImage.src = images[index].src;
    dialogImage.alt = images[index].alt;
    currentImageIndex = index;

    // Update active thumbnail in the popup dialog
    updateActiveThumbnail(index);
}

function updateMainGalleryImage(index) {
    const mainImage = document.querySelector('.gallery-main-image');

    if (!images[index] || !mainImage) {
        return;
    }

    mainImage.src = images[index].src;
    mainImage.alt = images[index].alt;

    document.querySelectorAll('.gallery-thumb').forEach(thumb => {
        const thumbImgIndex = parseInt(thumb.dataset.imageIndex) || 0;
        thumb.classList.toggle('active', thumbImgIndex === index);
    });
}

/**
 * Navigate gallery with slide animation
 * @param {number} direction  -1 for prev, 1 for next
 * @param {object} options    { invertAnimation: true } to reverse slide direction (for touch swipes)
 */
function navigateGallery(direction, options = {}) {
    if (isAnimating) return;

    // For touch swipes the animation should follow the finger direction,
    // which is opposite to the arrow-click animation
    const animDir = options.invertAnimation ? -direction : direction;

    let newIndex = currentImageIndex + direction;
    if (newIndex < 0) {
        newIndex = images.length - 1;
    } else if (newIndex >= images.length) {
        newIndex = 0;
    }

    const dialog = document.getElementById('gallery-dialog');
    const dialogImage = dialog?.querySelector('.gallery-dialog-image');
    if (!dialogImage || !dialog.open) {
        showImage(newIndex);
        return;
    }

    isAnimating = true;
    const outClass = animDir > 0 ? 'slide-out-right' : 'slide-out-left';
    const inClass = animDir > 0 ? 'slide-in-right' : 'slide-in-left';
    const container = dialogImage.parentElement;

    // Clone the old image and pin it exactly where the original sits
    const rect = dialogImage.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const ghost = dialogImage.cloneNode(true);
    ghost.classList.add('gallery-dialog-ghost');
    ghost.style.top = (rect.top - containerRect.top) + 'px';
    ghost.style.left = (rect.left - containerRect.left) + 'px';
    ghost.style.width = rect.width + 'px';
    ghost.style.height = rect.height + 'px';
    container.appendChild(ghost);

    // Position new image at its start offset (no transition)
    dialogImage.style.transition = 'none';
    dialogImage.classList.add(inClass);
    dialogImage.src = images[newIndex].src;
    dialogImage.alt = images[newIndex].alt;
    currentImageIndex = newIndex;
    updateActiveThumbnail(newIndex);

    // Force paint so both are in their start positions
    void dialogImage.offsetWidth;

    // Animate both simultaneously
    dialogImage.style.transition = '';
    dialogImage.classList.remove(inClass);
    ghost.classList.add(outClass);

    // Clean up after animation
    ghost.addEventListener('transitionend', () => {
        ghost.remove();
        isAnimating = false;
    }, { once: true });

    // Safety fallback in case transitionend doesn't fire
    setTimeout(() => {
        if (ghost.parentElement) ghost.remove();
        isAnimating = false;
    }, 400);
}

/**
 * Populate thumbnail strip in dialog
 */
function populateThumbnails() {
    const thumbnailContainer = document.querySelector('.gallery-dialog-thumbnails');
    if (!thumbnailContainer) return;

    thumbnailContainer.innerHTML = '';

    images.forEach((image, index) => {
        const thumb = document.createElement('button');
        thumb.className = 'gallery-dialog-thumb';
        thumb.setAttribute('aria-label', `View image ${index + 1}`);

        const img = document.createElement('img');
        img.src = image.thumb || image.src;
        img.alt = image.alt;

        thumb.appendChild(img);

        if (index === currentImageIndex) {
            thumb.classList.add('active');
        }

        thumb.addEventListener('click', () => showImage(index));
        thumbnailContainer.appendChild(thumb);
    });
}

function syncThumbnailAlignment() {
    const thumbnailContainer = document.querySelector('.gallery-dialog-thumbnails');
    if (!thumbnailContainer) return;

    const fitsViewport = thumbnailContainer.scrollWidth <= thumbnailContainer.clientWidth + 1;
    thumbnailContainer.classList.toggle('gallery-dialog-thumbnails--centered', fitsViewport);
}

/**
 * Update active thumbnail highlight
 */
function updateActiveThumbnail(index) {
    const thumbnails = document.querySelectorAll('.gallery-dialog-thumb');
    thumbnails.forEach((thumb, i) => {
        thumb.classList.toggle('active', i === index);
    });
}

/**
 * Swipe gesture support for mobile
 */
function setupSwipeGestures() {
    const dialog = document.getElementById('gallery-dialog');
    if (!dialog) return;

    let startX = 0;
    let startY = 0;
    let tracking = false;

    dialog.addEventListener('touchstart', (e) => {
        // Ignore swipes on thumbnails (allow normal scrolling)
        if (e.target.closest('.gallery-dialog-thumbnails')) return;
        startX = e.changedTouches[0].clientX;
        startY = e.changedTouches[0].clientY;
        tracking = true;
    }, { passive: true });

    dialog.addEventListener('touchend', (e) => {
        if (!tracking) return;
        tracking = false;

        const dx = e.changedTouches[0].clientX - startX;
        const dy = e.changedTouches[0].clientY - startY;

        // Only trigger if horizontal swipe is dominant and exceeds threshold
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
            if (dx < 0) {
                navigateGallery(1, { invertAnimation: true });  // Swipe left → next
            } else {
                navigateGallery(-1, { invertAnimation: true }); // Swipe right → prev
            }
        }
    }, { passive: true });
}
